import { useEffect, useRef, useState } from 'react';
import { mapUsPortfolioOrder } from '@/lib/polymarketUs/portfolioClient';
import { presentUsOrder } from '@/lib/polymarketUs/portfolioModel';
import type { UsPendingRequest, UsTradingClient } from '@/lib/polymarketUs/tradingClient';
import type { UsOrderRow, UsScope } from '@/lib/polymarketUs/types';
import { formatRecordedUtc } from './UsOrderTable';
import { usOrderReason } from '@/lib/polymarketUs/reason';

type Props = {
  client: Pick<UsTradingClient, 'setScope' | 'pending' | 'recover' | 'acknowledge'>;
  accountId: string;
  scope: UsScope;
  refreshRevision?: number;
  /** The shared ticket shows one recovery action, not a second order form. */
  compact?: boolean;
};
type Row = { request: UsPendingRequest; order?: UsOrderRow; error?: string };
const button = 'border-border rounded border px-3 py-1 text-xs disabled:opacity-50';
/** #1114 T8 quick references. */
const CHECK_REQUEST_TITLE = "Reads G1's saved result for this request; never resends.";
const DISMISS_REMINDER_TITLE = "Removes this reminder from this browser; doesn't touch the order.";
const reason = (e: unknown) => e && typeof e === 'object' && 'code' in e && typeof e.code === 'string'
  && /^(US|RELAY)_[A-Z0-9_]{1,100}$/.test(e.code) ? e.code : 'US_REQUEST_UNCONFIRMED';
const recorded = (ms: number) => Number.isFinite(new Date(ms).getTime()) ? `${formatRecordedUtc(ms)} UTC` : 'Unavailable';
const needsStatusCheck = (row: Row) => {
  if (!row.order || row.error) return true;
  const state = row.order.lifecycle?.state ?? row.order.receipt.status;
  return ['ready', 'submitted', 'pending', 'unknown', 'conflict', 'replaced'].includes(state);
};

/** Local request markers are not server intent IDs. This surface only reads
 * saved evidence; it never retries a submission or polls in the background. */
export function UsRequestRecovery({ client, accountId, scope, refreshRevision = 0, compact = false }: Props) {
  const visit = useRef(0);
  const inFlight = useRef(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const lastRefresh = useRef(refreshRevision);

  useEffect(() => {
    const mine = ++visit.current;
    inFlight.current = false;
    setRows([]); setError(null); setBusy(null);
    try {
      client.setScope({ accountId, scopeKey: scope.scopeKey, revision: scope.revision });
      setRows(client.pending().map(request => ({ request })));
    } catch (e) { setError(reason(e)); }
    return () => { visit.current = mine + 1; client.setScope(null); };
  }, [client, accountId, scope]);

  useEffect(() => {
    if (lastRefresh.current === refreshRevision) return;
    lastRefresh.current = refreshRevision;
    try {
      const pending = client.pending();
      setRows(current => {
        const known = new Map(current.map(row => [row.request.requestKey, row]));
        return pending.map(request => {
          const row = known.get(request.requestKey);
          return row && row.request.previewId === request.previewId && row.request.createdAtMs === request.createdAtMs
            ? { ...row, request } : { request };
        });
      });
      setError(null);
    } catch (e) { setError(reason(e)); }
  }, [client, refreshRevision]);

  async function check(requestKey: string) {
    if (inFlight.current) return;
    inFlight.current = true;
    const mine = visit.current;
    setBusy(requestKey);
    try {
      const result = await client.recover(requestKey);
      if (mine !== visit.current) return;
      // Reuse portfolio status semantics. The trading client's parsed receipt
      // omits cancellation and fee fields; neither is inferred here.
      const order = result.kind === 'receipt'
        ? mapUsPortfolioOrder({ ...result.order, cancel: null }, accountId, scope, 'own') : undefined;
      setRows(current => current.map(row => row.request.requestKey === requestKey
        ? { ...row, ...(order ? { order } : {}), error: result.kind === 'unconfirmed' ? result.code : undefined } : row));
    } catch (e) {
      if (mine === visit.current) setRows(current => current.map(row => row.request.requestKey === requestKey
        ? { ...row, error: reason(e) } : row));
    } finally {
      if (mine === visit.current) { inFlight.current = false; setBusy(null); }
    }
  }

  function dismiss(row: Row) {
    if (inFlight.current || !row.order) return;
    try {
      client.acknowledge(row.request.requestKey);
      setRows(current => current.filter(r => r.request.requestKey !== row.request.requestKey));
    } catch (e) {
      setRows(current => current.map(r => r.request.requestKey === row.request.requestKey ? { ...r, error: reason(e) } : r));
    }
  }

  if (!error && rows.length === 0) return null;
  const contents = <>
    <h2 className={compact ? 'sr-only' : 'text-sm font-semibold'}>Your saved US requests ({rows.length})</h2>
    <p className="text-muted-foreground mt-1 text-xs">These requests were saved before submission. Check their recorded result before placing another order. Checking never resends an order.</p>
    {error && <p role="alert" className="mt-2 break-words text-xs" title={error}>Unable to read saved requests: {usOrderReason(error)}. Refresh account access to try again.</p>}
    <ul className="mt-2 max-h-80 space-y-2 overflow-y-auto pr-1">
      {rows.map(row => {
        const key = row.request.requestKey;
        const view = row.order ? presentUsOrder(row.order) : null;
        return <li key={key} className="border-border min-w-0 border-t pt-2 text-xs">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="text-muted-foreground">Saved {recorded(row.request.createdAtMs)}</span>
            <strong>{view?.status ?? 'Result unconfirmed'}</strong>
          </div>
          {row.order && view && <div className="mt-1 break-words">
            <p>{row.order.target.label} · {row.order.action.toUpperCase()} {row.order.quantity} contracts at ${row.order.outcomePrice}</p>
            <p>Filled {view.filled} · Remaining {view.remaining} · Recorded {recorded(row.order.observedAtMs)}</p>
            {view.statusReason && <p>{view.statusReason}</p>}
            <p className="text-muted-foreground">Saved in order history. Open Position Tracker for current status and available actions.</p>
          </div>}
          {row.error && <p role="alert" className="mt-1 break-words" title={row.error}>Latest lookup unconfirmed: {usOrderReason(row.error)}. {row.order ? 'The last recorded result is retained.' : 'This does not prove the order was not sent.'}</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            <button className={button} disabled={busy !== null} title={CHECK_REQUEST_TITLE} onClick={() => { void check(key); }}>
              {busy === key ? 'Checking request…' : 'Check request'}
            </button>
            {row.order && <button className={button} disabled={busy !== null} title={DISMISS_REMINDER_TITLE} onClick={() => dismiss(row)}>Dismiss saved reminder</button>}
          </div>
          <details className="mt-1 text-muted-foreground">
            <summary className="cursor-pointer">Request details</summary>
            <p className="break-all font-mono">Request {key}</p>
            {row.order && <p className="break-all font-mono">Intent {row.order.intentId} · Venue order {row.order.orderId ?? 'Unavailable'}</p>}
            {row.error && <p className="break-all font-mono">{row.error}</p>}
          </details>
        </li>;
      })}
    </ul>
  </>;
  return <section aria-label="Your saved US requests" className={compact ? 'min-w-0 text-xs' : 'border-border mb-3 min-w-0 rounded border p-3'}>
    {compact ? <details data-testid="us-request-recovery-disclosure">
      <summary className="cursor-pointer text-[11px] leading-4 text-amber-500 light:text-amber-700">
        {error ? 'Saved requests unavailable — review before retrying'
          : rows.some(needsStatusCheck)
            ? `${rows.length} saved request${rows.length === 1 ? '' : 's'} — check status before placing again`
            : `${rows.length} checked request${rows.length === 1 ? '' : 's'} — review or dismiss`}
      </summary>
      {contents}
    </details> : contents}
  </section>;
}
